import React, { FC } from 'react'

import { Typography } from '@smarty-nx/ds'
import { liteClient as algoliasearch } from 'algoliasearch/lite'
import 'instantsearch.css/themes/reset.css'
import type { Hit as AlgoliaHit } from 'instantsearch.js'
import {
  Highlight,
  Hits,
  InstantSearch,
  SearchBox,
  useSearchBox,
} from 'react-instantsearch'

import classes from './search.module.scss'
import { CONFIG } from '../../config'
import SearchIcon from '../../static/images/icons/search.svg'

// Uses React InstantSearch library for Algolia https://www.algolia.com/doc/guides/building-search-ui/what-is-instantsearch/react/
const algoliaClient = algoliasearch(
  CONFIG.ALGOLIA_APP_ID as string,
  CONFIG.ALGOLIA_APP_KEY as string
)

// To prevent empty search requests being sent on page load we wrap the AlgoliaClient in a proxy to enable additional logic
// https://www.algolia.com/doc/guides/building-search-ui/going-further/conditional-requests/react/
const searchClient = {
  ...algoliaClient,
  search(requests) {
    if (requests.every(({ params }) => !params?.query)) {
      return Promise.resolve({
        results: requests.map(() => ({
          hits: [],
          nbHits: 0,
          nbPages: 0,
          page: 0,
          processingTimeMS: 0,
          hitsPerPage: 0,
          exhaustiveNbHits: false,
          query: '',
          params: '',
        })),
      })
    }
    return algoliaClient.search(requests)
  },
}

interface HitData {
  url: string
  title: string
  description?: string
}

// A Hit is the imported component which provides a list of search results from Intercom (via Algolia)
const Hit: FC<{ hit: AlgoliaHit<HitData> }> = ({ hit }) => (
  <a
    className={classes.searchResultLink}
    href={hit.url}
    target="_blank"
    rel="noreferrer"
  >
    <Highlight
      className={classes.styledHighlight}
      attribute="title"
      hit={hit}
    />
    {hit.description != null && (
      <Typography
        variant="Kollektif/Small"
        className={classes.searchResultDescription}
      >
        {hit.description}
      </Typography>
    )}
  </a>
)

// Only shows results once the user has actually typed a query, mirroring the
// previous onChange-driven behaviour that's no longer supported by <SearchBox>.
const SearchResults: FC = () => {
  const { query } = useSearchBox()

  return query !== '' ? (
    <Hits className={classes.styledHits} hitComponent={Hit} />
  ) : null
}

export const Search: FC = () => (
  <div className={classes.searchWrapper}>
    <InstantSearch searchClient={searchClient} indexName="Articles">
      {/* Some UI changes of the Searchbox are handled via props & we're using...
             - placeholder > to customise the placeholder text
             - submitIconComponent > over riding the submit magnifying icon with our own SVG
             - the reset button is hidden via CSS (.styled-search-box) to match own designs
        */}
      <SearchBox
        className={classes.styledSearchBox}
        placeholder="Search by keywords..."
        submitIconComponent={() => <img src={SearchIcon} alt="" />}
      />
      <SearchResults />
    </InstantSearch>
  </div>
)

export default Search
